"use client";

import { SentryFeatureTags, logException } from "@punchup/sentry";

import { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { getFollowedComedians } from "@/data/follows";
import { getUserPaidSpecials } from "@/data/v2/client/user/specials";
import { getUserPaidSubscriptionTiers } from "@punchup/data/client/user/paid-subscriptions";
import { useQuery } from "@tanstack/react-query";

interface UserContentAccessData {
  paidSpecialIds: string[];
  subscribedComedianIds: string[];
  followedComedianIds: string[];
  tiersByComedianId: Record<string, number>;
}

/**
 * Hook that fetches and caches user-level content access data.
 * Shared across all useSpecialUnlockedForUser instances so that
 * paid specials, subscriptions, and follows are only fetched once per user.
 */
export function useUserContentAccess({
  userId,
}: {
  /** undefined = still loading, null = not logged in, string = user ID */
  userId: string | null | undefined;
}) {
  return useQuery<UserContentAccessData>({
    queryKey: ["user-content-access", userId],
    queryFn: async () => {
      if (!userId) {
        return {
          paidSpecialIds: [],
          subscribedComedianIds: [],
          followedComedianIds: [],
          tiersByComedianId: {},
        };
      }

      const supabase = createClientComponentClient<Database>();

      try {
        const [purchasedSpecials, tiersByComedianId, followedComedians] =
          await Promise.all([
            getUserPaidSpecials(),
            getUserPaidSubscriptionTiers(),
            getFollowedComedians({ supabase }),
          ]);

        return {
          paidSpecialIds: purchasedSpecials.map((s) => s.id),
          subscribedComedianIds: Object.keys(tiersByComedianId),
          followedComedianIds: followedComedians.map((f) => f.comedian_id),
          tiersByComedianId,
        };
      } catch (error) {
        logException(error as Error, {
          extra: {
            data: "Error fetching user content access data",
          },
          tags: {
            feature: SentryFeatureTags.Specials,
          },
        });
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: userId !== undefined, // don't fetch while user auth is still loading
  });
}
